import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Iter52.93 · T1 · Los parámetros de lista viajan repetidos (`lista=A&lista=B`),
// que es lo que lee FastAPI, en vez de `lista[]=A`.
export const saasApi = axios.create({ baseURL: API, paramsSerializer: { indexes: null } });
saasApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('mobilsafe_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

saasApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      const rutaLogin = '/admin';
      const yaEnLogin = typeof window !== 'undefined' && window.location.pathname.startsWith(rutaLogin) && !localStorage.getItem('mobilsafe_token');
      localStorage.removeItem('mobilsafe_token');
      localStorage.removeItem('mobilsafe_user');
      localStorage.removeItem('mobilsafe_auth');
      // Aviso al usuario + redirect (una sola vez por ventana)
      if (typeof window !== 'undefined' && !yaEnLogin && !window.__mobilsafe_401_shown) {
        window.__mobilsafe_401_shown = true;
        try {
          // toast dinámico sin dependencia dura del hook (sonner es global)
          const { toast } = require('sonner');
          toast.error('Sesión expirada · vuelve a iniciar sesión');
        } catch (_) { /* fallback: solo redirect */ }
        setTimeout(() => { window.location.href = rutaLogin; }, 800);
      }
    }
    return Promise.reject(error);
  }
);

export const errText = (e) => e?.response?.data?.detail || e?.message || 'Error inesperado';

// ------------------------------------------------------------------
// Cache stale-while-revalidate para respuestas de sólo lectura del
// módulo Operaciones SaaS. Reduce el reflow al cambiar de pestaña:
// se pinta al instante con la última foto y se refresca en background.
// TTL por defecto 60 s. Invalidar tras acciones admin con
// `invalidateSaasCache()` (llamado automáticamente).
// ------------------------------------------------------------------
const _swrStore = new Map();  // key -> { at, data, inflight }

export function invalidateSaasCache(prefix = '') {
  if (!prefix) return _swrStore.clear();
  for (const k of Array.from(_swrStore.keys())) {
    if (k.startsWith(prefix)) _swrStore.delete(k);
  }
}

/**
 * Devuelve `{data, stale, refresh}` para un endpoint cacheable.
 * - Si hay cache fresco (< ttlMs) → devuelve data inmediatamente y NO refetch.
 * - Si hay cache viejo o inexistente → devuelve data (o null) + dispara
 *   fetch en background con onFresh(dataNueva) para hidratar la UI.
 * - Deduplica requests concurrentes con la misma clave.
 */
export async function apiCached(url, {ttlMs = 60000, onFresh, force = false} = {}) {
  const key = url;
  const now = Date.now();
  const entry = _swrStore.get(key);
  const fresh = entry && (now - entry.at) < ttlMs;
  if (fresh && !force) {
    return { data: entry.data, stale: false };
  }
  // Devolvemos la foto vieja (si existe) inmediatamente y refetch en background.
  const staleData = entry?.data ?? null;
  if (!entry?.inflight) {
    entry && (entry.inflight = true);
    _swrStore.set(key, { at: entry?.at || 0, data: staleData, inflight: true });
    (async () => {
      try {
        const r = await saasApi.get(url);
        _swrStore.set(key, { at: Date.now(), data: r.data, inflight: false });
        if (onFresh) onFresh(r.data);
      } catch (e) {
        const cur = _swrStore.get(key) || {};
        _swrStore.set(key, { ...cur, inflight: false });
      }
    })();
  }
  return { data: staleData, stale: true };
}

export const money = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
export const fmtDate = (v) => v ? new Date(`${String(v).slice(0,10)}T00:00:00`).toLocaleDateString('es-ES') : '—';
// Iter52.53 · B3 · Helper: 'YYYY-MM' → mes anterior 'YYYY-MM' (usado por
// el aviso "Uso SaaS · <mes M-1>" en la pantalla de prefactura).
export const mesAnterior = (periodo) => {
  const s = String(periodo || '').slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(s)) return s;
  let [y, m] = s.split('-').map(Number);
  m -= 1;
  if (m === 0) { m = 12; y -= 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
};

// IMPL-074 · Rol actual leído del localStorage (guardado por login)
export const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('mobilsafe_user') || 'null'); } catch { return null; }
};
export const isSuperAdmin = () => {
  const u = currentUser();
  return u && ['super_admin', 'superadmin'].includes(u.role);
};

// Iter52.62 · B6 · Descarga del informe PDF de una carga de datos.
// Se llama automáticamente al terminar de aplicar; si el navegador bloquea
// la descarga, la pantalla mantiene visible el enlace manual.
export const descargarInformeCarga = async (informeId, { silencioso = false } = {}) => {
  if (!informeId) return false;
  try {
    const r = await saasApi.get(`/saas/informes-carga/${informeId}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `informe_carga_${informeId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    if (!silencioso) {
      try { const { toast } = require('sonner'); toast.error('No se pudo descargar el informe PDF'); } catch (_) {}
    }
    return false;
  }
};
